import { Box, InputBase, Tooltip, styled } from '@mui/material';
import { useEffect, useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { computeSteppedValue } from '~/components/Inputs/numberInputStepping';
import {
    ambiguousDecimalMessage,
    ambiguousDecimalReadings,
    decimalEntryText,
    parseDecimalInput,
} from '~/utils/decimalInput';
import { OverlayLabel } from './OverlayLabel';

const ClickableValue = styled(OverlayLabel)`
    cursor: text;
    user-select: none;
    min-width: 32px;

    &:hover {
        text-decoration: underline;
    }
`;

/**
 * Edit-mode wrapper used when a unit suffix is shown so the input and the
 * suffix render side-by-side at the same baseline.
 */
const EditWrapper = styled(Box)`
    display: inline-flex;
    align-items: baseline;
    min-width: 32px;
`;

const ValueInput = styled(InputBase)`
    font-size: 11px;
    line-height: 11px;
    font-variant-numeric: tabular-nums;
    font-family: 'Roboto Mono';
    color: inherit;
    background: transparent;
    & input {
        padding: 0;
        height: 11px;
        line-height: 11px;
        background: transparent;
        width: 5ch;
        // Match the display label's left-alignment so the digit doesn't
        // shift horizontally when the user clicks into edit mode.
        text-align: left;
    }
    &.Mui-error {
        color: ${({ theme }) => theme.palette.error.main};
        text-decoration: underline wavy;
    }
`;

/**
 * Suffix-mode input variant: narrower so the input followed by the suffix
 * span occupies roughly the same width as the display label.
 */
const SuffixedValueInput = styled(ValueInput)`
    & input {
        width: 3ch;
    }
`;

export interface EditableNumericValueProps {
    /** Current numeric value (in display units). */
    value: number;
    /**
     * Called once when the user commits a parsed, validated, distinct value.
     * The component does not call this on blur/Enter if validation fails or
     * the value is unchanged — see `validate` and the equality check below.
     */
    onCommit: (newValue: number) => void;
    /**
     * Optional validation hook. Returning `false` rejects the input — the
     * component reverts to the prior value without calling `onCommit`.
     * If omitted, any number the entry names is accepted.
     */
    validate?: (newValue: number) => boolean;
    /**
     * Called with a typed value that `validate` rejected: a finite number
     * that differs from the current value. The field reverts to the current
     * value either way; this lets the caller say why.
     */
    onReject?: (rejectedValue: number) => void;
    /** Render-only formatter for display mode. Defaults to `String(value)`. */
    formatDisplay?: (value: number) => string;
    /** Accessible label for the underlying input. */
    ariaLabel?: string;
    /**
     * Keyboard stepping while editing: ArrowUp / ArrowDown move the typed
     * value to the next multiple of `step` (counted from `min`, or 0), and
     * stop at `min` / `max`. A typed value is never clamped to them; that
     * stays with `validate`.
     */
    min?: number;
    max?: number;
    step?: number;
    /** Optional test id forwarded to both display + edit elements. */
    testId?: string;
    /** Suffix appended after the value in display mode (e.g. ' %'). */
    unitSuffix?: string;
    /**
     * Called when the user enters edit mode. Use this to suppress external
     * state updates from clobbering the buffered string mid-edit if needed.
     */
    onEditStart?: () => void;
    /** Called when the user exits edit mode (commit or cancel). */
    onEditEnd?: () => void;
    /**
     * If true, mount directly in edit mode. Useful when the parent already
     * detected a click (e.g. after a drag-vs-click gesture filter) and wants
     * the user to start typing immediately without an extra click.
     */
    defaultEditing?: boolean;
}

/**
 * Click-to-edit numeric value with Enter/blur commit and Escape cancel. It is
 * a pure keyboard and click-to-edit input, with no drag gesture of its own.
 *
 * The entry is read by {@link parseDecimalInput}: a point or a comma is the
 * decimal separator, whatever the browser's locale. An entry that names no
 * number (`1,5,3`, `abc`) is marked invalid while it is being typed, Enter
 * does not accept it, and leaving the field reverts it; it is never read as
 * the number its leading digits spell. An entry that may also be a whole
 * number written with a thousands separator (`1,500`), where `validate`
 * accepts the whole number, is refused the same way, and a hint names the
 * two numbers and how to type each (see {@link ambiguousDecimalReadings}).
 *
 * Nothing is committed while the user types: only Enter or leaving the field
 * commits, so no part of an entry is ever applied on its way to being typed.
 *
 * Validation must happen in the caller via `validate`. The component will not
 * silently clamp values; it reverts to the prior value on validation failure
 * so the user always sees a known-good number, and reports the rejected
 * value through `onReject`.
 */
const EditableNumericValue = ({
    value,
    onCommit,
    validate,
    onReject,
    formatDisplay,
    ariaLabel,
    min,
    max,
    step,
    testId,
    unitSuffix = '',
    onEditStart,
    onEditEnd,
    defaultEditing = false,
}: EditableNumericValueProps) => {
    const [isEditing, setIsEditing] = useState(defaultEditing);
    // The buffer is seeded with the value as an entry the field reads back
    // to it (`0.0000005`, not `5e-7`), so an edit starts from a number.
    const [inputValue, setInputValue] = useState(decimalEntryText(value));
    // Whether the buffer holds text the user typed, rather than a value the
    // field wrote itself (the seeded value, an arrow step): only typed text
    // can be ambiguous, since the field's own text is the number it shows.
    const [isTypedText, setIsTypedText] = useState(false);
    const hintId = useId();

    // Keep the buffered string in sync with the prop while NOT editing. We
    // intentionally avoid stomping the user's in-flight buffer if a parallel
    // state update lands during an edit (mirrors the WW/WC behaviour).
    useEffect(() => {
        if (!isEditing) {
            setInputValue(decimalEntryText(value));
        }
    }, [value, isEditing]);

    const enterEditMode = (): void => {
        setInputValue(decimalEntryText(value));
        setIsTypedText(false);
        setIsEditing(true);
        onEditStart?.();
    };

    const exitEditMode = (): void => {
        setIsEditing(false);
        onEditEnd?.();
    };

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        setInputValue(e.target.value);
        setIsTypedText(true);
    };

    const typedValue = parseDecimalInput(inputValue);
    const ambiguity =
        isTypedText && typedValue !== null
            ? ambiguousDecimalReadings(
                  inputValue,
                  (candidate) => !validate || validate(candidate)
              )
            : null;
    // The number the entry names, when it names exactly one.
    const enteredValue = ambiguity === null ? typedValue : null;
    const isRefused = enteredValue === null;

    const handleInputBlur = (): void => {
        if (enteredValue !== null && enteredValue !== value) {
            if (!validate || validate(enteredValue)) {
                onCommit(enteredValue);
            } else {
                onReject?.(enteredValue);
            }
        }
        exitEditMode();
    };

    const handleKeyDown = (e: React.KeyboardEvent): void => {
        if (e.key === 'Enter') {
            // An entry that names no number, or no single one, stays open
            // and marked, rather than closing onto the previous value as if
            // it were accepted.
            if (!isRefused) {
                handleInputBlur();
            }
        } else if (e.key === 'Escape') {
            setInputValue(decimalEntryText(value));
            setIsTypedText(false);
            exitEditMode();
        } else if (
            (e.key === 'ArrowUp' || e.key === 'ArrowDown') &&
            step !== undefined &&
            enteredValue !== null
        ) {
            // The key steps the value; it must not also move the caret to
            // the start of the text, where the next digit typed would land
            // in front of the stepped value.
            e.preventDefault();
            const anchor = min ?? 0;
            const stepped = computeSteppedValue(
                enteredValue,
                e.key === 'ArrowUp' ? 1 : -1,
                step,
                true,
                anchor
            );
            const bounded = Math.min(
                Math.max(stepped, min ?? -Infinity),
                max ?? Infinity
            );
            // Written with the digits of both the step and the anchor the
            // grid counts from, so a stepped value (0.05 + 0.1) is shown,
            // and committed, as itself and not rounded to the step's digits.
            setInputValue(
                bounded.toFixed(
                    Math.max(fractionDigits(step), fractionDigits(anchor))
                )
            );
            setIsTypedText(false);
        }
    };

    if (isEditing) {
        const inputProps = {
            value: inputValue,
            onChange: handleInputChange,
            onBlur: handleInputBlur,
            onKeyDown: handleKeyDown,
            autoFocus: true,
            // Text rather than `number`: a number input hands back an empty
            // string for an entry the browser's locale does not read, so the
            // field could neither take a decimal comma nor say it was wrong.
            type: 'text',
            error: isRefused,
            inputProps: {
                'aria-label': ariaLabel,
                'aria-invalid': isRefused,
                'aria-describedby': isRefused ? hintId : undefined,
                'data-testid': testId,
                inputMode: 'decimal' as const,
                onFocus: (e: React.FocusEvent<HTMLInputElement>) =>
                    e.target.select(),
            },
        };
        const editor = unitSuffix ? (
            <EditWrapper>
                <SuffixedValueInput {...inputProps} />
                <OverlayLabel as="span">{unitSuffix}</OverlayLabel>
            </EditWrapper>
        ) : (
            <ValueInput {...inputProps} />
        );
        return (
            <Tooltip
                id={hintId}
                open={isRefused}
                disableFocusListener
                disableHoverListener
                disableTouchListener
                describeChild
                placement="top"
                arrow
                title={
                    !isRefused ? (
                        ''
                    ) : ambiguity !== null ? (
                        <FormattedMessage
                            {...ambiguousDecimalMessage(inputValue, ambiguity)}
                        />
                    ) : (
                        <FormattedMessage id="enterANumber" />
                    )
                }
            >
                {editor}
            </Tooltip>
        );
    }

    const displayText = formatDisplay
        ? formatDisplay(value)
        : `${String(value)}${unitSuffix}`;

    return (
        <ClickableValue
            onClick={enterEditMode}
            data-testid={testId}
            aria-label={ariaLabel}
            role="button"
            tabIndex={0}
            onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    enterEditMode();
                }
            }}
        >
            {displayText}
        </ClickableValue>
    );
};

/** The number of fraction digits `value` is written with (0.25 → 2, 1 → 0). */
function fractionDigits(value: number): number {
    const fraction = value.toFixed(10).replace(/0+$/, '').split('.')[1];
    return fraction?.length ?? 0;
}

export default EditableNumericValue;
